import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { useEndPlatformSupportSession } from "../tenants/hooks/usePlatformTenantSession";

import { Button } from "@/components/Button";
import type { RootState } from "@/redux/store";

// Published as a CSS var (rather than threaded through props/context) so any part
// of the app shell — Sidebar in particular — can offset itself below the banner
// without knowing about platformSession at all. Kept in sync with the banner's
// real rendered height (not a guessed constant) so it still holds when the text
// wraps to two lines on narrow viewports or with a long tenant name.
const BANNER_HEIGHT_VAR = "--platform-banner-h";

export function PlatformSupportSessionBanner() {
  const platformSession = useSelector((state: RootState) => state.platformSession);
  const endSupportSession = useEndPlatformSupportSession();
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!platformSession.active) {
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, "0px");
      return;
    }

    const el = bannerRef.current;
    if (!el) return;

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        BANNER_HEIGHT_VAR,
        `${el.offsetHeight}px`,
      );
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, "0px");
    };
  }, [platformSession.active]);

  if (!platformSession.active) return null;

  return (
    <div
      ref={bannerRef}
      className="sticky top-0 z-[60] flex flex-wrap items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950 sm:px-6"
    >
      <span>
        <strong className="font-semibold">Platform Owner Access</strong>
        <span className="mx-2 opacity-60" aria-hidden="true">
          |
        </span>
        You are viewing <strong>{platformSession.tenantName}</strong> as Platform
        Administrator.
      </span>
      <Button
        variant="neutral"
        size="sm"
        className="border-amber-950/30 bg-amber-400/40 text-amber-950 hover:bg-amber-400/60"
        loading={endSupportSession.isPending}
        onClick={() => endSupportSession.mutate()}
      >
        Return to Platform
      </Button>
    </div>
  );
}
